'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useIntl } from 'react-intl';
import type { Download } from '@packages/gql/generated/shopping/graphql';
import { downloadItemMessages } from './messages';

/**
 * Props for rendering a single download link entry.
 */
export type DownloadItemProps = Pick<Download, 'link' | 'type'> & {
  /**
   * Decides whether the download icon should be rendered.
   * @default true
   */
  renderIcon?: boolean;
  /**
   * Optional - additional text next to the message.
   */
  linkTextExtension?: number | string;
};

/**
 * Renders a localized download link that opens in a new tab.
 *
 * The link can be displayed in an icon style (default) or as plain underlined text.
 *
 * Renders nothing if the given download type does not have a matching i18n message.
 *
 * @example
 * ```tsx
 * <DownloadItem type={type} link={link} />
 * ```
 */
export const DownloadItem = ({
  link,
  type,
  renderIcon = true,
  linkTextExtension,
}: DownloadItemProps) => {
  const { formatMessage } = useIntl();

  if (type in downloadItemMessages === false) {
    return null;
  }

  return (
    <Link
      href={link}
      openInNewTab
      className={
        renderIcon ? 'text-size-body-sm flex items-center gap-1' : 'text-size-body-xs underline'
      }
    >
      {renderIcon && <span className="icon-emp-pdf decor text-size-headline-2xl" />}
      {formatMessage(downloadItemMessages[type])} {linkTextExtension}
      {/* this is only for A11Y needs (CROSS-1372) */}
      <span className="sr-only">
        {formatMessage({
          id: `efficiencyFlagDownloads.a11y.text`,
          defaultMessage: '(PDF, öffnet in neuem Tab)',
        })}
      </span>
    </Link>
  );
};
